import React from 'react';
import {
  glassTransitions,
  GlassVariantKey,
  glassVariants,
  type GlassVariant,
} from '@/styles/liquidGlass';
import { Button, ButtonProps, styled } from '@mui/material';

interface GlassButtonProps extends ButtonProps {
  glassVariant?: GlassVariantKey;
  hoverable?: boolean;
  animated?: boolean;
}

const StyledGlassButton = styled(Button, {
  shouldForwardProp: (prop) =>
    !['glassVariant', 'hoverable', 'animated'].includes(prop as string),
})<GlassButtonProps>(
  ({
    theme,
    color = 'primary',
    variant = 'text',
    glassVariant = 'medium',
    hoverable = true,
    animated = true,
  }) => {
    const glassKey = (glassVariant ?? 'medium') as GlassVariantKey;
    const glassStyle: GlassVariant = glassVariants[glassKey];
    const colorKey = color ?? 'primary';
    const labelColor = (() => {
      switch (colorKey) {
        case 'error':
          return theme.palette.error.main;
        case 'inherit':
          return 'inherit';
        case 'secondary':
          return theme.palette.secondary.main;
        case 'success':
          return theme.palette.success.main;
        case 'info':
          return theme.palette.info.main;
        case 'warning':
          return theme.palette.warning.main;
        default:
          return theme.palette.primary.main;
      }
    })();

    const isContained = variant === 'contained';
    const hoverBackground = hoverable
      ? glassStyle.hoverBackground ?? glassStyle.background
      : glassStyle.background;
    const hoverBoxShadow = hoverable
      ? glassStyle.hoverBoxShadow ?? glassStyle.boxShadow
      : glassStyle.boxShadow;

    return {
      background: glassStyle.background,
      border: glassStyle.border,
      backdropFilter: glassStyle.backdropFilter,
      boxShadow: glassStyle.boxShadow,
      borderRadius: theme.spacing(1.5),
      transition: animated ? glassTransitions.smooth : 'none',
      transform: 'translateZ(0)', // Enable hardware acceleration
      position: 'relative',
      overflow: 'hidden',
      ...(!isContained
        ? {
            '&&': {
              color: labelColor,
              '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                color: 'currentColor',
              },
            },
          }
        : {}),

      // Remove default button styles
      '&:hover': {
        ...(!isContained
          ? {
              '&&': { color: labelColor },
            }
          : {}),
        background: hoverBackground,
        boxShadow: hoverBoxShadow,
        transform: hoverable ? 'translateY(-1px)' : 'none',
      },

      '&:active': {
        transform: 'translateY(0px)',
      },

      ...(!isContained
        ? {
            '&.Mui-disabled': {
              color: theme.palette.action.disabled,
            },
          }
        : {}),

      // Glass overlay effect
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 'inherit',
        background:
          'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
        pointerEvents: 'none',
        zIndex: 1,
      },

      // Ensure content is above glass overlay
      '& > *': {
        position: 'relative',
        zIndex: 2,
      },

      // Disable ripple effect for cleaner glass look
      '& .MuiTouchRipple-root': {
        display: 'none',
      },
    };
  }
);

export const GlassButton: React.FC<GlassButtonProps> = ({
  glassVariant = 'medium',
  hoverable = true,
  animated = true,
  variant = 'text',
  children,
  sx,
  ...props
}) => {
  return (
    <StyledGlassButton
      variant={variant}
      glassVariant={glassVariant}
      hoverable={hoverable}
      animated={animated}
      sx={sx}
      {...props}
    >
      {children}
    </StyledGlassButton>
  );
};

export default GlassButton;
